import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { ChartDataLabel } from 'locale/es';
import moment from 'moment';
import { ParametersTypes } from 'resources/ParametersTypes'
import PatientsService from 'services/PatientsService'
import {
  convertToLocalTime,
  createBarChartObject,
  filterAndSortDates,
  getHoursArray,
  getTotalValuesPerHour,
} from 'utils/HelperFunctions'
import { addToCache } from './cacheReducer';

export const fetchCaloriesDataIfNeeded = createAsyncThunk(
  'caloriesStats/fetchCaloriesDataIfNeeded',
  async ({ patientId, selectedDate, type, name }, { getState, dispatch }) => {
    const startDate = moment(selectedDate).subtract(1, 'months').startOf('month').format("YYYY-MM-DDTHH:mm:ss[Z]");
    const endDate = moment(selectedDate).format(("YYYY-MM-DDTHH:mm:ss[Z]"));
    const key = `calories-${type}-${patientId}`;

    const cache = getState().cache.apiResponses[key];
    const selectedMoment = moment(selectedDate);

    if (cache && selectedMoment.isBetween(moment(cache.startDate), moment(cache.endDate), undefined, '[]')) {
      dispatch(updateStateWithCachedData({ type, data: cache.data, status: 'cached' }));
      return cache.data;
    } else {
      const { data, status } = await PatientsService.getObservations(name, patientId, startDate, endDate);
      if (status === 200) {
        const existingData = cache ? cache.data : [];
        const combinedData = [...existingData, ...data];
        dispatch(addToCache({ key, data: combinedData, startDate, endDate }));
        return { data: combinedData, type, status };
      }
    }
  }
)

const initialState = {
  lastThreeWeeksPatientData: {},
  lastYearsPatientData: {},
  lastTwoMonthsPatientData: {},
  perHourChartTotalCaloriesInDay: {},
}

const caloriesStatsSlice = createSlice({
  name: 'caloriesStats',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchCaloriesDataIfNeeded.fulfilled, (state, action) => {
      const { type, data, status } = action.payload;
      switch (type) {
        case "CALORIES_LAST_TWO_MONTHS":
          state.lastTwoMonthsPatientData = { data, status };
          break;
        default:
          break;
      }
    })
  },
  reducers: {
    updateStateWithCachedData(state, action) {
      const { type, data, status } = action.payload;
      switch (type) {
        case "CALORIES_LAST_TWO_MONTHS":
          state.lastTwoMonthsPatientData = { data, status };
          break;
        default:
          break;
      }
    },
    setPatientStatsLastThreeWeeksPerHour(state, action) {
      return { ...state, lastThreeWeeksPatientData: action.payload }
    },
    setPatientStatsLastYearsPerHour(state, action) {
      return { ...state, lastYearsPatientData: action.payload }
    },
    setChartTotalCaloriesInDay(state, action) {
      const selectedDate = action.payload
      const { data } = state.lastTwoMonthsPatientData

      const dateFormat = "H";

      let valuesToDisplay

      try {
        const dataConvertedToLocalTime = convertToLocalTime(data)

        const caloriesStatsSelectedDaySortedPerHour = filterAndSortDates(dataConvertedToLocalTime, selectedDate, 'day')

        const valuesPerDay = getTotalValuesPerHour(getHoursArray(), caloriesStatsSelectedDaySortedPerHour, dateFormat)

        valuesToDisplay = Object.values(valuesPerDay).map(dataPoint => dataPoint)
      } catch {
        valuesToDisplay = []
      }

      const labels = getHoursArray().map(hour => hour.length === 1 ? `0${hour}` : hour)
      const dataLabel = ChartDataLabel.TOTAL_CALORIES

      const chartObject = createBarChartObject(valuesToDisplay, dataLabel, labels)

      return { ...state, perHourChartTotalCaloriesInDay: chartObject }
    },
    resetCaloriesState(state, action) {
      return initialState
    }
  },
})

export const {
  setPatientStatsLastThreeWeeksPerHour, setPatientStatsLastYearsPerHour, setChartTotalCaloriesInDay, updateStateWithCachedData, resetCaloriesState } = caloriesStatsSlice.actions

export const getCaloriesStatsLastTwoMonths = (patientId, selectedDate) => {
  return async (dispatch, getState) => {
    const { name, type } = ParametersTypes.CALORIES_LAST_TWO_MONTHS;
    const params = {
      patientId,
      selectedDate,
      type,
      name,
    }
    dispatch(fetchCaloriesDataIfNeeded(params));
  }
}

export const changeDateLastTwoMonths = (selectedDate) => {
  return (dispatch) => {
    dispatch(setChartTotalCaloriesInDay(selectedDate))
  }
}

export default caloriesStatsSlice.reducer